import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Tab, Tabs } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { Loader, Modal } from '@/components'
import { useAppContext } from '@/hooks'
import { useConfirmationModal } from '@/pages/App/components/ConfirmationService'
import type {
	AclEntryDto,
	AclEntryDtoRead,
	Permission,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	useCreateStructureAclMutation,
	useDeleteStructureAclMutation,
	useGetEffectiveAclQuery,
	useGetGroupsQuery,
	useGetStructureAclQuery,
	useUpdateStructureAclMutation,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { logError } from '@/utils/errorHandling'

import { useTreePanelOperations } from '../TreePanelContext'
import { StructureAclEvaluateTab } from './StructureAclEvaluateTab'
import { StructureAclManageTab } from './StructureAclManageTab'
import {
	ACL_PERMISSIONS,
	type AclEditState,
	EMPTY_PARENT_EFFECTIVE_ACL,
	entryToPermissionState,
	getInitialPermissionState,
	type PermissionChoice,
	permissionStateToPayload,
	permissionToLabelKey,
} from './structureAclUtils'

type StructureAclModalProps = {
	/** After ACL mutations; use to reload the open detail node when tree refresh is a no-op. */
	onAclMutated?: (structureId: number) => void
	onClose: () => void
	onOpenParentPermissions?: () => void
	onOpenStructurePermissions?: (structureId: number) => void
	open: boolean
	parentStructureId?: number
	parentStructureName?: string
	structureId: number
	structureName?: string
}

export const StructureAclModal = ({
	open,
	onClose,
	structureId,
	structureName,
	parentStructureId,
	parentStructureName,
	onOpenParentPermissions,
	onOpenStructurePermissions,
	onAclMutated,
}: StructureAclModalProps) => {
	const { t } = useAppContext()
	const { openConfirmModal } = useConfirmationModal()
	const { refreshSystemsSilent } = useTreePanelOperations()

	const [mainTab, setMainTab] = useState(0)
	const [editState, setEditState] = useState<AclEditState>(null)
	const [selectedGroupName, setSelectedGroupName] = useState('')
	const [permissionState, setPermissionState] = useState<
		Record<Permission, PermissionChoice>
	>(() => getInitialPermissionState())

	useEffect(() => {
		if (!open) setMainTab(0)
	}, [open])

	const silentRefreshTree = useCallback(() => {
		void refreshSystemsSilent().catch((e) => {
			logError(e, { operation: 'StructureAclModal.refreshSystemsSilent' })
		})
	}, [refreshSystemsSilent])

	const refreshTreeAfterAclChange = useCallback(() => {
		silentRefreshTree()
		onAclMutated?.(structureId)
		window.setTimeout(() => {
			silentRefreshTree()
		}, 500)
	}, [silentRefreshTree, onAclMutated, structureId])

	const {
		data: entries = [],
		isLoading,
		refetch,
	} = useGetStructureAclQuery({ structureId }, { skip: !open || !structureId })
	const { data: effectiveEntries = [], isLoading: effectiveLoading } =
		useGetEffectiveAclQuery({ structureId }, { skip: !open || !structureId })
	const {
		data: parentEffectiveData,
		fulfilledTimeStamp: parentEffectiveAclFulfilledAt,
	} = useGetEffectiveAclQuery(
		{ structureId: parentStructureId! },
		{ skip: !open || !parentStructureId },
	)
	const parentEffectiveEntries =
		parentEffectiveData ?? EMPTY_PARENT_EFFECTIVE_ACL

	const parentEffectiveDataRef = useRef(parentEffectiveData)
	parentEffectiveDataRef.current = parentEffectiveData
	const { data: groups = [] } = useGetGroupsQuery(
		{ pageSize: 1000, pageNumber: 1 },
		{ skip: !open },
	)
	const [createEntry, { isLoading: isCreating }] =
		useCreateStructureAclMutation()
	const [updateEntry, { isLoading: isUpdating }] =
		useUpdateStructureAclMutation()
	const [deleteEntry, { isLoading: isDeleting }] =
		useDeleteStructureAclMutation()

	const groupNames = useMemo(
		() => groups.map((g) => g.name ?? '').filter(Boolean),
		[groups],
	)
	const existingGroupNames = useMemo(
		() => new Set(entries.map((e) => e.groupName).filter(Boolean)),
		[entries],
	)
	const availableGroupNamesForAdd = useMemo(
		() => groupNames.filter((name) => !existingGroupNames.has(name)),
		[groupNames, existingGroupNames],
	)

	const permissionOptions = useMemo(
		() =>
			ACL_PERMISSIONS.map((permission) => ({
				permission,
				label: t(permissionToLabelKey[permission]),
			})),
		[t],
	)

	useEffect(() => {
		if (editState?.type !== 'add' || !selectedGroupName) return
		const pe = parentEffectiveDataRef.current ?? EMPTY_PARENT_EFFECTIVE_ACL
		if (pe.length === 0) {
			setPermissionState(getInitialPermissionState())
			return
		}
		const parentEntry = pe.find(
			(e) => (e.groupName ?? '') === selectedGroupName,
		)
		setPermissionState(
			parentEntry
				? entryToPermissionState(parentEntry)
				: getInitialPermissionState(),
		)
	}, [editState?.type, selectedGroupName, parentEffectiveAclFulfilledAt])

	const resetForm = useCallback(() => {
		setEditState(null)
		setSelectedGroupName('')
		setPermissionState(getInitialPermissionState())
	}, [])

	const handleOpenAdd = useCallback(() => {
		setEditState({ type: 'add' })
		setSelectedGroupName('')
		setPermissionState(getInitialPermissionState())
	}, [])

	const handleOpenEdit = useCallback((entry: AclEntryDtoRead) => {
		setEditState({ type: 'edit', entry })
		setSelectedGroupName(entry.groupName ?? '')
		setPermissionState(entryToPermissionState(entry))
	}, [])

	const handleCancelEdit = useCallback(() => {
		resetForm()
	}, [resetForm])

	const handleOpenStructurePermissions = useCallback(
		(targetStructureId: number) => {
			setMainTab(0)
			resetForm()
			onOpenStructurePermissions?.(targetStructureId)
		},
		[onOpenStructurePermissions, resetForm],
	)

	const setPermissionChoice = useCallback(
		(p: Permission, choice: PermissionChoice) => {
			setPermissionState((prev) => ({ ...prev, [p]: choice }))
		},
		[],
	)

	const buildPayload = useCallback(
		(): AclEntryDto => ({
			groupName: selectedGroupName || undefined,
			...permissionStateToPayload(permissionState),
		}),
		[selectedGroupName, permissionState],
	)

	const handleSaveAdd = useCallback(async () => {
		if (!selectedGroupName.trim()) {
			toast.warning(t('ACL_SELECT_GROUP'))
			return
		}
		try {
			await createEntry({
				structureId,
				aclEntryDto: buildPayload(),
			}).unwrap()
			toast.success(t('ACL_ENTRY_ADDED'))
			refetch()
			refreshTreeAfterAclChange()
			resetForm()
		} catch {
			// Error handled by RTK Query
		}
	}, [
		createEntry,
		structureId,
		buildPayload,
		refetch,
		refreshTreeAfterAclChange,
		resetForm,
		selectedGroupName,
		t,
	])

	const handleSaveEdit = useCallback(async () => {
		if (!editState || editState.type !== 'edit' || editState.entry.id == null)
			return
		try {
			await updateEntry({
				structureId,
				aclId: editState.entry.id,
				aclEntryDto: buildPayload(),
			}).unwrap()
			toast.success(t('ACL_ENTRY_UPDATED'))
			refetch()
			refreshTreeAfterAclChange()
			resetForm()
		} catch {
			// Error handled by RTK Query
		}
	}, [
		editState,
		updateEntry,
		structureId,
		buildPayload,
		refetch,
		refreshTreeAfterAclChange,
		resetForm,
		t,
	])

	const handleDelete = useCallback(
		async (entry: AclEntryDtoRead) => {
			if (entry.id == null) return
			try {
				await openConfirmModal({
					catchOnCancel: true,
					title: t('REMOVE'),
					description: t('ACL_ENTRY_DELETE_CONFIRM', [entry.groupName ?? '']),
				})
				await deleteEntry({ structureId, aclId: entry.id }).unwrap()
				toast.success(t('ACL_ENTRY_REMOVED'))
				refetch()
				refreshTreeAfterAclChange()
			} catch {
				// User cancelled or API error
			}
		},
		[
			deleteEntry,
			openConfirmModal,
			refetch,
			refreshTreeAfterAclChange,
			structureId,
			t,
		],
	)

	const headerTitle = structureName
		? `${t('PERMISSIONS')} – ${structureName}`
		: t('PERMISSIONS')

	const isSaving = isCreating || isUpdating

	return (
		<Modal
			open={open}
			onClose={onClose}
			header={headerTitle}
			closeButtonVisible
			dialogId="structure-acl-modal"
			contentStyle={{
				maxWidth: 1200,
				width: '95vw',
				maxHeight: '85vh',
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}
			bodyStyle={{
				overflow: 'auto',
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				minWidth: 0,
				minHeight: 0,
				flex: 1,
			}}
			footer={
				<>
					{mainTab === 0 && !editState && (
						<Button
							variant="outlined"
							size="small"
							startIcon={<AddIcon />}
							onClick={handleOpenAdd}
							data-testid="structure-acl-button-add-entry"
						>
							{t('ADD_ACL_ENTRY')}
						</Button>
					)}
					<Button
						variant="contained"
						onClick={onClose}
						data-testid="structure-acl-button-close"
					>
						{t('CLOSE')}
					</Button>
				</>
			}
		>
			<Box
				data-testid="modal-structure-acl-body"
				onClick={(e) => e.stopPropagation()}
				sx={{ width: '100%', minWidth: 0 }}
			>
				{isLoading ? (
					<Loader loaded={false} />
				) : (
					<Box
						sx={{
							minWidth: 0,
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Tabs
							value={mainTab}
							onChange={(_, v) => setMainTab(v)}
							variant="scrollable"
							scrollButtons="auto"
							data-testid="structure-acl-tabs"
							sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
						>
							<Tab label={t('ACL_TAB_MANAGE')} data-testid="structure-acl-tab-manage" />
							<Tab
								label={t('ACL_TAB_EVALUATE')}
								data-testid="structure-acl-tab-evaluate"
							/>
						</Tabs>

						{mainTab === 1 && (
							<StructureAclEvaluateTab
								structureId={structureId}
								enabled={open && mainTab === 1}
								onOpenStructurePermissions={handleOpenStructurePermissions}
							/>
						)}

						{mainTab === 0 && (
							<StructureAclManageTab
								parentStructureId={parentStructureId}
								parentStructureName={parentStructureName}
								onOpenParentPermissions={onOpenParentPermissions}
								effectiveEntries={effectiveEntries}
								effectiveLoading={effectiveLoading}
								entries={entries}
								editState={editState}
								selectedGroupName={selectedGroupName}
								setSelectedGroupName={setSelectedGroupName}
								permissionState={permissionState}
								setPermissionChoice={setPermissionChoice}
								availableGroupNamesForAdd={availableGroupNamesForAdd}
								groupNames={groupNames}
								parentEffectiveEntries={parentEffectiveEntries}
								permissionOptions={permissionOptions}
								onOpenEdit={handleOpenEdit}
								onDelete={handleDelete}
								onCancelEdit={handleCancelEdit}
								onSaveAdd={handleSaveAdd}
								onSaveEdit={handleSaveEdit}
								isSaving={isSaving}
								isDeleting={isDeleting}
							/>
						)}
					</Box>
				)}
			</Box>
		</Modal>
	)
}
